import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { rptService } from '../../../services/rptService';
import { authService } from '../../../authService';
import PropertyPaymentTab from './components/PropertyPaymentTab';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  FormControlLabel,
  TablePagination,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Payment as PaymentIcon,
  Calculate as CalculateIcon,
  History as HistoryIcon,
  Print as PrintIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Help as HelpIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

// Helper for rounding to 2 decimal places
const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

const Payments = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const preSelectedPropertyId = searchParams.get('property_id');
  const [currentUser, setCurrentUser] = useState(null);

  // Sidebar state
  const [showPropertyList, setShowPropertyList] = useState(true);

  // Tab state
  const [openTabs, setOpenTabs] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [barangayFilter, setBarangayFilter] = useState('');
  const [allBarangays, setAllBarangays] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', content: '', onConfirm: null });
  const [showORModal, setShowORModal] = useState(false);
  const [orNumber, setORNumber] = useState('');
  const [payerName, setPayerName] = useState('');
  const [orNumberError, setORNumberError] = useState('');
  const orModalResolveRef = useRef(null);

  // Floating Cart
  const [cartItems, setCartItems] = useState([]);
  const cartTotal = cartItems.reduce((sum, item) => sum + (parseFloat(item.total_amount ?? item.amount ?? 0) || 0), 0);
  const [cartOpen, setCartOpen] = useState(false);
  const addToCart = (item) => {
    setCartItems(prev => {
      const key = `${item.property_id}-${item.snapshot_id || ''}`;
      const existingIndex = prev.findIndex(i => `${i.property_id}-${i.snapshot_id || ''}` === key);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = item;
        return next;
      }
      return [...prev, item];
    });
  };
  const removeFromCart = (keyIndex) => {
    setCartItems(prev => prev.filter((_, idx) => idx !== keyIndex));
  };
  const clearCart = () => setCartItems([]);

  const loadCurrentUser = async () => {
    try {
      const user = authService.getUserSession();
      if (user) {
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadPreSelectedProperty = async () => {
    try {
      setLoading(true);
      const result = await rptService.getPropertyById(preSelectedPropertyId);
      if (result.success) {
        handleSelectProperty(result.data);
      } else {
        showSnackbar('Property not found', 'error');
      }
    } catch (error) {
      console.error('Error loading pre-selected property:', error);
      showSnackbar('Error loading property', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOwners();
    loadCurrentUser();
    loadAllBarangays();
    if (preSelectedPropertyId) {
      loadPreSelectedProperty();
    }
  }, []);

  // Derived payment ratio for display (helper)
  const normalizeBarangay = (b) => {
    if (!b) return '';
    return b.trim().toUpperCase();
  };

  const formatBarangayLabel = (b) => {
    if (!b) return '';
    // Capitalize first letter of each word
    return b.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [barangayFilter]);

  // Load properties with server-side pagination
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await rptService.getProperties({
          page,
          pageSize: rowsPerPage,
          search: debouncedSearch,
          barangay: barangayFilter,
          sortBy: 'td',
          sortDir: 'asc'
        });
        if (result.success) {
          setProperties(result.data || []);
          setTotalProperties(result.count || 0);
        }
      } catch (error) {
        console.error('Error loading properties:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [page, rowsPerPage, debouncedSearch, barangayFilter]);

  const loadAllBarangays = async () => {
    try {
      const result = await rptService.getAllBarangays();
      if (result.success) {
        const list = (result.data || []).map(v => String(v).trim()).filter(Boolean);
        const unique = Array.from(new Set(list)).sort();
        setAllBarangays(unique);
      }
    } catch (error) {
      console.error('Error loading barangays:', error);
    }
  };

  const loadOwners = async () => {
    try {
      const result = await rptService.getAllOwners();
      if (result.success) {
        setOwners(result.data || []);
      }
    } catch (error) {
      console.error('Error loading owners:', error);
    }
  };

  const requestPaymentInfo = () => {
    return new Promise((resolve) => {
      setORNumber('');
      setPayerName('');
      setORNumberError('');
      orModalResolveRef.current = resolve;
      setShowORModal(true);
    });
  };

  const getOwnerName = (ownerId) => {
    const owner = owners.find(o => o.owner_id === ownerId);
    return owner?.owner_name || 'Unknown';
  };

  const handleSelectProperty = async (property) => {
    // Check if property is already open
    const existingIndex = openTabs.findIndex(t => t.property_id === property.property_id);
    
    if (existingIndex !== -1) {
      // Switch to existing tab
      setActiveTabIndex(existingIndex);
    } else {
      // Open new tab
      setOpenTabs([...openTabs, property]);
      setActiveTabIndex(openTabs.length); // New tab is at the end
    }
    
    // On mobile, hide list when property is selected
    if (isMobile) {
      setShowPropertyList(false);
    }
  };

  const handleCloseTab = (index, e) => {
    if (e) e.stopPropagation();
    
    const newTabs = [...openTabs];
    newTabs.splice(index, 1);
    setOpenTabs(newTabs);

    // Adjust active tab index
    if (newTabs.length === 0) {
      setActiveTabIndex(0);
    } else if (activeTabIndex >= newTabs.length) {
      setActiveTabIndex(newTabs.length - 1);
    } else if (index < activeTabIndex) {
      // If we closed a tab before the active one, decrement index
      setActiveTabIndex(activeTabIndex - 1);
    }
    // If we closed the active tab (index === activeTabIndex), we naturally fall back to the same index (which is now the next tab) or the last one if we were at the end (handled by activeTabIndex >= newTabs.length check).
  };

  const handleORSubmit = () => {
    if (!orNumber.trim()) {
      setORNumberError('OR Number is required');
      return;
    }
    setShowORModal(false);
    if (orModalResolveRef.current) {
      orModalResolveRef.current({ orNumber, payerName });
      orModalResolveRef.current = null;
    }
  };

  const handleORCancel = () => {
    setShowORModal(false);
    if (orModalResolveRef.current) {
      orModalResolveRef.current(null);
      orModalResolveRef.current = null;
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const showConfirm = (title, content, onConfirm) => {
    setConfirmDialog({ open: true, title, content, onConfirm });
  };

  const handleConfirm = async () => {
    const onConfirm = confirmDialog.onConfirm;
    setConfirmDialog(prev => ({ ...prev, open: false }));

    if (!onConfirm) return;

    try {
      await onConfirm();
    } catch (error) {
      console.error('Confirm action failed:', error);
      showSnackbar(`Unable to complete action: ${error?.message || 'Unknown error'}`, 'error');
    }
  };

  const barangays = React.useMemo(() => {
    const map = new Map();
    (allBarangays || []).forEach(v => {
      const n = normalizeBarangay(v);
      if (n && !map.has(n)) map.set(n, v);
    });
    const arr = Array.from(map.values()).map(formatBarangayLabel);
    return arr.sort((a, b) => a.localeCompare(b));
  }, [allBarangays]);

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: '0.875rem' }}>
              <Link 
                underline="hover" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/portal/dashboard')}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Portal
              </Link>
              <Link 
                underline="hover" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/portal/property-tax/dashboard')}
              >
                RPT System
              </Link>
              <Typography color="text.primary" sx={{ fontWeight: 500 }}>Payments</Typography>
            </Breadcrumbs>
            
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1
            }}>
              <PaymentIcon fontSize="large" sx={{ color: '#3b82f6' }} /> Real Property Tax Payments
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
              Calculate taxes, process payments, and view payment history.
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Left Panel - Property Selection */}
          <Grid item xs={12} md={4} sx={{ display: showPropertyList ? 'block' : 'none' }}>
            <Card sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <TextField
                  fullWidth
                  placeholder="Search properties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />
                
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Barangay</InputLabel>
                  <Select
                    value={barangayFilter}
                    label="Filter by Barangay"
                    onChange={(e) => setBarangayFilter(e.target.value)}
                  >
                    <MenuItem value=""><em>All Barangays</em></MenuItem>
                    {barangays.map((b) => (
                      <MenuItem key={b} value={b}>{b}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>

              <List sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
                {loading ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">Loading properties...</Typography>
                  </Box>
                ) : properties.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">No properties found.</Typography>
                  </Box>
                ) : (
                  properties.map((property) => (
                    <React.Fragment key={property.property_id}>
                      <ListItemButton
                        selected={openTabs.some(t => t.property_id === property.property_id)}
                        onClick={() => handleSelectProperty(property)}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          borderBottom: '1px solid #f1f5f9',
                          py: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                          <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                            {property.td_number}
                          </Typography>
                          <Chip 
                            label={property.classification_name || 'N/A'} 
                            size="small" 
                            color={property.classification_name === 'Residential' ? 'success' : 'warning'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', mb: 0.5 }}>
                          {property.owner_name || getOwnerName(property.owner_id)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                          AV: ₱{parseFloat(property.assessed_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <HomeIcon sx={{ fontSize: 14 }} /> {property.barangay}
                        </Typography>
                      </ListItemButton>
                    </React.Fragment>
                  ))
                )}
              </List>
              
              <TablePagination
                component="div"
                count={totalProperties}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                sx={{ borderTop: '1px solid #e2e8f0' }}
              />
            </Card>
          </Grid>

          {/* Right Panel - Property Tabs */}
          <Grid item xs={12} md={8}>
            {isMobile && !showPropertyList && (
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={() => setShowPropertyList(true)}
                sx={{ mb: 2 }}
              >
                Back to List
              </Button>
            )}

            {openTabs.length > 0 ? (
              <Box>
                <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={activeTabIndex}
                    onChange={(e, v) => setActiveTabIndex(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="property tabs"
                  >
                    {openTabs.map((tabProperty, index) => (
                      <Tab
                        key={tabProperty.property_id}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {tabProperty.td_number}
                            </Typography>
                            <IconButton
                              size="small"
                              component="span"
                              onClick={(e) => handleCloseTab(index, e)}
                              sx={{ 
                                p: 0.2, 
                                ml: 0.5,
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main', bgcolor: 'rgba(239, 68, 68, 0.1)' } 
                              }}
                            >
                              <CloseIcon fontSize="small" sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        }
                        id={`property-tab-${index}`}
                      />
                    ))}
                  </Tabs>
                </Paper>

                {openTabs.map((tabProperty, index) => (
                  <Box
                    key={tabProperty.property_id}
                    role="tabpanel"
                    hidden={activeTabIndex !== index}
                    sx={{ display: activeTabIndex === index ? 'block' : 'none' }}
                  >
                    <PropertyPaymentTab
                      property={tabProperty}
                      currentUser={currentUser}
                      showSnackbar={showSnackbar}
                      showConfirm={showConfirm}
                      requestPaymentInfo={requestPaymentInfo}
                      onAddCartItem={(payload) => addToCart(payload)}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Card sx={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  <PaymentIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h5" gutterBottom>No Property Selected</Typography>
                  <Typography variant="body1">
                    Select a property from the list to calculate tax or process payment.
                  </Typography>
                  {isMobile && (
                    <Button 
                      variant="contained" 
                      onClick={() => setShowPropertyList(true)}
                      sx={{ mt: 3 }}
                    >
                      Select Property
                    </Button>
                  )}
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Floating Cart */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1200
        }}
      >
        {!cartOpen ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShoppingCartIcon />}
            onClick={() => setCartOpen(true)}
            sx={{ boxShadow: 3 }}
          >
            Cart ({cartItems.length})
          </Button>
        ) : (
        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', minWidth: 320, boxShadow: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">Payment Cart</Typography>
            <Stack direction="row" spacing={1}>
              <Chip 
                label={`Items: ${cartItems.length}`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={`Total: ₱${parseFloat(cartTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                color="primary"
                size="small"
              />
              <IconButton size="small" onClick={() => setCartOpen(false)} aria-label="hide cart">
                <ExpandLessIcon />
              </IconButton>
            </Stack>
          </Box>
          {cartItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Cart is empty. Click Calculate Tax to add items.</Typography>
          ) : (
            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
              {cartItems.map((item, idx) => (
                <ListItem
                  key={`${item.property_id}-${item.snapshot_id || idx}`}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => removeFromCart(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {item.td_number || 'TD'} • {item.owner_name || ''}{item.barangay ? ` • ${item.barangay}` : ''}{item.years ? ` • Years: ${item.years}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`FULL PAYMENT: ₱${parseFloat(item.full_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`DISC/PEN: ₱${parseFloat(item.discpen_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        color={(item.discpen_amount ?? 0) < 0 ? 'success' : 'error'}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`TOTAL: ₱${parseFloat(item.total_amount ?? item.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        color="primary"
                      />
                    </Stack>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined" onClick={clearCart} disabled={cartItems.length === 0}>Clear</Button>
            <Button size="small" variant="contained" color="primary" disabled={cartItems.length === 0}
              onClick={() => showSnackbar(`Cart prepared • ${cartItems.length} item(s) • ₱${parseFloat(cartTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'info')}>
              Prepare
            </Button>
          </Stack>
        </Paper>
        )}
      </Box>

      {/* Shared Dialogs */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>{confirmDialog.content}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showORModal} onClose={handleORCancel}>
        <DialogTitle>Enter Payment Details</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Official Receipt Number"
            fullWidth
            variant="outlined"
            value={orNumber}
            onChange={(e) => {
              setORNumber(e.target.value);
              setORNumberError('');
            }}
            error={!!orNumberError}
            helperText={orNumberError}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Paid By (Optional)"
            placeholder="Leave blank to use Owner's Name"
            fullWidth
            variant="outlined"
            value={payerName}
            onChange={(e) => setPayerName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleORCancel}>Cancel</Button>
          <Button onClick={handleORSubmit} variant="contained" color="primary">Submit</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;
